import {FC, useContext} from "react";
import {useNavigate, useParams} from "react-router-dom";
import {Box, Button, CircularProgress} from "@mui/material";
import {makeStyles} from "tss-react/mui";
import {ModuleNotAvailable} from "@buildwitt/shapeshift-common/v1/module";
import {useGetLessonForCourse} from "@/app2/shared/data";
import {useGetCourseCurriculum} from "@/app2/shared/data";
import {LessonPageContent} from "./LessonPageContent";
import useDevCycleSdk from "@/hooks/useDevCycleSdk";
import {LocaleContext, resolveLocale} from "@/app2/shared/contexts";

const useStyles = makeStyles()(() => ({
	lessonPage: {},
	goToNextLesson: {
		marginTop: "25px"
	},
	notAvailableContainer: {
		width: "100%",
		textAlign: "center"
	}
}));

/**
 * Lesson Page Container
 * 
 * Fetches lesson data and handles:
 * - Loading states
 * - 403 (locked) errors
 * - Navigation to next available lesson
 * 
 * Uses React Query instead of LessonDataProvider context.
 */
export const LessonPage: FC = () => {
	const {classes} = useStyles();
	const {courseSlug, moduleSlug: lessonSlug} = useParams<{ courseSlug: string; moduleSlug: string }>();
	const navigate = useNavigate();
	const [{locale}] = useContext(LocaleContext);
	const resolvedLocale = resolveLocale(locale);
	// Fetch lesson data
	const {data: lessonData, isLoading, isError, error} = useGetLessonForCourse(
		courseSlug!,
		lessonSlug!,
		{locale: resolvedLocale}
	);

	// React Query's refetchOnMount (default) handles stale data when returning to the page.
	// Avoid manual refetch in useEffect - it caused 3x requests (refetchOnMount + effect + Strict Mode).

	// Fetch curriculum to find next available lesson (if 403)
	const {data: curriculumData} = useGetCourseCurriculum(courseSlug!, {locale: resolvedLocale});

	// Find first available (unlocked, incomplete) lesson
	const firstAvailableModule = curriculumData?.items.find(
		item => !item.complete && !item.isLocked
	);

	// Loading state: only show spinner on initial load. During background refetch (isFetching + lessonData),
	// keep showing the lesson so the video player doesn't remount.
	if (isLoading) {
		return (
			<Box
				display="flex"
				justifyContent="center"
				alignItems="center"
				height="100%"
				minHeight="300px"
			>
				<CircularProgress />
			</Box>
		);
	}

	// 403 Forbidden (locked lesson)
	if (isError && error?.response?.status === 403) {
		return (
			<Box className={classes.notAvailableContainer}>
				<ModuleNotAvailable>
					{error?.response?.data?.message || "Lessons must be completed in sequence."}
				</ModuleNotAvailable>
				{firstAvailableModule && (
					<Button
						className={classes.goToNextLesson}
						onClick={() => navigate(`/course/${courseSlug}/${firstAvailableModule.type}/${firstAvailableModule.slug}`)}
					>
						Go to next available lesson
					</Button>
				)}
				{!firstAvailableModule && (
					<Button
						className={classes.goToNextLesson}
						onClick={() => navigate("/home")}
					>
						Go back to My Learning
					</Button>
				)}
			</Box>
		);
	}

	// Other errors
	if (isError) {
		return (
			<Box className={classes.notAvailableContainer}>
				<ModuleNotAvailable>
					Unable to load lesson. Please try again.
				</ModuleNotAvailable>
			</Box>
		);
	}

	// Success - render lesson content
	if (lessonData) {
		return (
			<Box className={classes.lessonPage}>
				<LessonPageContent lesson={lessonData} />
			</Box>
		);
	}

	// Fallback
	return null;
};

